import React from 'react';
import {
  AlertTriangle,
  BookOpen,
  ClipboardCheck,
  FileText,
  FlaskConical,
  FolderGit2,
  GitBranch,
  Lightbulb,
  Network,
  Package,
  Play,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { type SubNavItem } from '../../../design-system/v2';
import { FlowNav } from './FlowNav';
import { detectActivityFromPath } from './projectActivities';
import {
  OPERATE_DOMAINS,
  OPERATE_TAB_TO_DOMAIN,
  PHASE0_TABS,
  PHASE1_TABS,
  type OperateDomainId,
  type Phase0TabId,
  type Phase1TabId,
  operateDomainNavigationPath,
  phase0TabPath,
  phase1TabPath,
  resolveOperateDomain,
  resolveOperateTab,
} from './projectPhases';

const PHASE0_ICONS: Record<Phase0TabId, LucideIcon> = {
  repository: FolderGit2,
  artifacts: Package,
  changes: GitBranch,
  review: ClipboardCheck,
};

const PHASE1_ICONS: Record<Phase1TabId, LucideIcon> = {
  insights: Lightbulb,
  graph: Network,
  knowledge: BookOpen,
};

const OPERATE_DOMAIN_ICONS: Record<OperateDomainId, LucideIcon> = {
  incidents: AlertTriangle,
  requests: FileText,
  engineering: Wrench,
  executions: Play,
  quality: FlaskConical,
};

function iconOf(Icon: LucideIcon): React.ReactElement {
  return <Icon strokeWidth={1.8} aria-hidden />;
}

/**
 * Section tabs for the active lifecycle activity (Discover / Understand /
 * Operate) as a FlowNav. Renders nothing outside the
 * lifecycle, and selects no tab when the path doesn't match one, so a page is
 * never shown under a wrong highlighted tab.
 */
const PhaseContextNav: React.FC = () => {
  const { projectId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const incidentId = searchParams.get('incident');
  const requestId = searchParams.get('request');
  const activity = detectActivityFromPath(location.pathname);

  if (!activity) return null;
  const pathParts = location.pathname.split('/').filter(Boolean);

  if (activity === 'discover') {
    const idx = pathParts.indexOf('phase-0');
    const segment = idx >= 0 ? pathParts[idx + 1] : undefined;
    const value = PHASE0_TABS.some((t) => t.id === segment) ? (segment as Phase0TabId) : false;
    const items: SubNavItem<Phase0TabId>[] = PHASE0_TABS.map((t) => ({
      value: t.id,
      label: t.label,
      icon: iconOf(PHASE0_ICONS[t.id]),
    }));
    return (
      <FlowNav
        ariaLabel="Discover sections"
        items={items}
        value={value}
        onChange={(id) => navigate(phase0TabPath(projectId, id))}
      />
    );
  }

  if (activity === 'understand') {
    const idx = pathParts.indexOf('phase-1');
    // graph/explorer, graph/engine and graph/engine/:view all belong to the Graph tab.
    const segment = idx >= 0 ? pathParts[idx + 1] : undefined;
    const value = PHASE1_TABS.some((t) => t.id === segment) ? (segment as Phase1TabId) : false;
    const items: SubNavItem<Phase1TabId>[] = PHASE1_TABS.map((t) => ({
      value: t.id,
      label: t.label,
      icon: iconOf(PHASE1_ICONS[t.id]),
    }));
    return (
      <FlowNav
        ariaLabel="Understand sections"
        items={items}
        value={value}
        onChange={(id) => navigate(phase1TabPath(projectId, id))}
      />
    );
  }

  // Operate: only highlight a domain when the path names a known Operate tab —
  // resolveOperateTab() falls back to "incidents" for anything else.
  const operateIdx = pathParts.indexOf('operate');
  const rawOperateSegment = operateIdx >= 0 ? pathParts[operateIdx + 1] : undefined;
  const knownOperateTab =
    rawOperateSegment === undefined || rawOperateSegment in OPERATE_TAB_TO_DOMAIN;
  const activeDomain = resolveOperateDomain(resolveOperateTab(location.pathname));
  const items: SubNavItem<OperateDomainId>[] = OPERATE_DOMAINS.map((d) => ({
    value: d.id as OperateDomainId,
    label: d.label,
    icon: iconOf(OPERATE_DOMAIN_ICONS[d.id as OperateDomainId]),
  }));

  return (
    <FlowNav
      ariaLabel="Operate sections"
      items={items}
      value={operateIdx >= 0 && knownOperateTab ? (activeDomain.id as OperateDomainId) : false}
      onChange={(id) =>
        navigate(operateDomainNavigationPath(projectId, id, { incidentId, requestId }))
      }
    />
  );
};

export default PhaseContextNav;
